// src/components/Garage.js
import React, { useState, useEffect, useContext } from 'react';
import { TeamPlanContext } from '../App';
import { 
  collection, query, where, onSnapshot, addDoc, 
  orderBy, doc, updateDoc, getDocs, writeBatch 
} from "firebase/firestore";
import { db } from '../firebaseConfig'; 
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
//import { format } from 'date-fns';
import { pdf } from '@react-pdf/renderer';
import VehicleHistoryPdf from './VehicleHistoryPdf';

import QRCode from "react-qr-code";

import {
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
  Paper,
  Autocomplete,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

// --- IMPORT ICONE ---
import AddIcon from '@mui/icons-material/Add';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
// --------------------

const rowStyle = { 
    display: 'flex', 
    flexDirection: { xs: 'column', md: 'row' }, 
    gap: 2.5, 
    mb: 2.5 
};

// Helper formattazione date
const formatDateForExcel = (timestamp) => {
  if (!timestamp) return '';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  } catch (e) { return ''; }
};

function Garage({ teamId, userRole, userId }) {
  const { t } = useTranslation('garage');
  const { isTrial } = useContext(TeamPlanContext);
  
  // Stati
  const [vehicles, setVehicles] = useState([]);
  const [teamData, setTeamData] = useState(null); // Aggiunto stato teamData
  const [isLoading, setIsLoading] = useState(true);
  
  const [marca, setMarca] = useState('');
  const [modello, setModello] = useState('');
  const [numeroGara, setNumeroGara] = useState('');
  const [categoria, setCategoria] = useState('GT'); // Nuova categoria
  const [dryWeight, setDryWeight] = useState(''); 
  const [passportLink, setPassportLink] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null); 
  const [selectedTemplate, setSelectedTemplate] = useState(null); 
  
  const [driversList, setDriversList] = useState([]);
  const [templatesList, setTemplatesList] = useState([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(true);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);

  // Stati Modali
  const [qrVehicle, setQrVehicle] = useState(null); 
  const [editVehicle, setEditVehicle] = useState(null); 
  const [isExporting, setIsExporting] = useState(false);

  const isManager = userRole === 'admin' || userRole === 'Engineer';
  const isRestrictedUser = userRole === 'Meccanico' || userRole === 'Magazziniere';

  // --- Caricamento Dati (Invariato per veicoli, aggiunto team) ---
  useEffect(() => {
    if (!teamId) { setIsLoading(false); return; }
    setIsLoading(true);
    let unsubscribeVehicles = () => {};
    let unsubscribeTeam = () => {};

    // Recupera i dati del team
    unsubscribeTeam = onSnapshot(doc(db, 'teams', teamId), (snap) => {
      if (snap.exists()) {
        setTeamData(snap.data());
      }
    });

    const sortAndSetVehicles = (vehiclesList) => {
      vehiclesList.sort((a, b) => {
        const numA = parseFloat(a.numeroGara);
        const numB = parseFloat(b.numeroGara);
        if (!isNaN(numA) && !isNaN(numB)) { return numA - numB; }
        if (!isNaN(numA)) return -1;
        if (!isNaN(numB)) return 1;
        return (a.numeroGara || '').localeCompare(b.numeroGara || '');
      });
      setVehicles(vehiclesList);
    };

    if (isManager) {
      const vehiclesQuery = query(collection(db, "vehicles"), where("teamId", "==", teamId));
      unsubscribeVehicles = onSnapshot(vehiclesQuery, (qs) => {
        const list = qs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        sortAndSetVehicles(list);
        setIsLoading(false);
      });
    } else if (isRestrictedUser) {
      const assignmentsQuery = query(collection(db, "vehicleAssignments"), where("teamId", "==", teamId), where("userId", "==", userId));
      onSnapshot(assignmentsQuery, (as) => {
          const ids = as.docs.map(doc => doc.data().vehicleId);
          if (ids.length === 0) { setVehicles([]); setIsLoading(false); return; }
          const vQuery = query(collection(db, "vehicles"), where("teamId", "==", teamId), where("__name__", "in", ids));
          unsubscribeVehicles = onSnapshot(vQuery, (vs) => {
            const list = vs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            sortAndSetVehicles(list);
            setIsLoading(false);
          });
      });
    } else {
       const vQuery = query(collection(db, "vehicles"), where("teamId", "==", teamId));
       unsubscribeVehicles = onSnapshot(vQuery, (qs) => {
        const list = qs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        sortAndSetVehicles(list);
        setIsLoading(false);
      });
    }
    return () => { unsubscribeVehicles(); unsubscribeTeam(); };
  }, [teamId, userRole, userId, isManager, isRestrictedUser]);

  useEffect(() => {
    if (!teamId || !isManager) { setIsLoadingDrivers(false); return; }
    setIsLoadingDrivers(true);
    const q = query(collection(db, "drivers"), where("teamId", "==", teamId), orderBy("nomeCompleto", "asc"));
    onSnapshot(q, (qs) => {
      setDriversList(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingDrivers(false);
    });
  }, [teamId, isManager]);

  useEffect(() => {
    if (!teamId || !isManager) { setIsLoadingTemplates(false); return; }
    setIsLoadingTemplates(true);
    const q = query(collection(db, "setupTemplates"), where("teamId", "==", teamId), orderBy("templateName", "asc"));
    onSnapshot(q, (qs) => {
      setTemplatesList(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingTemplates(false);
    });
  }, [teamId, isManager]);


  // --- AZIONI ---

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    if (!teamId || !marca || !modello || !selectedTemplate) { alert(t('add_error')); return; };
    try {
      await addDoc(collection(db, "vehicles"), {
        teamId: teamId, marca: marca, modello: modello, numeroGara: numeroGara,
        categoria: categoria,
        dryWeight: Number(dryWeight) || null,
        createdAt: new Date(),
        mainDriverId: selectedDriver ? selectedDriver.id : null,
        mainDriverName: selectedDriver ? selectedDriver.nomeCompleto : null,
        passportLink: passportLink.trim(),
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.templateName
      });
      setMarca(''); setModello(''); setNumeroGara(''); setDryWeight('');
      setCategoria('GT');
      setSelectedDriver(null); setPassportLink(''); setSelectedTemplate(null); 
    } catch (error) { console.error("Error adding vehicle:", error); }
  };

  // 1. Apri Modale QR
  const handleOpenQR = (e, vehicle) => {
    e.preventDefault(); e.stopPropagation();
    setQrVehicle(vehicle);
  };

  // 2. Apri Modale Edit
  const handleOpenEdit = (e, vehicle) => {
    e.preventDefault(); e.stopPropagation();
    const driverObj = driversList.find(d => d.id === vehicle.mainDriverId) || null;
    const tmplObj = templatesList.find(t => t.id === vehicle.templateId) || null;
    
    setEditVehicle({
      ...vehicle,
      categoria: vehicle.categoria || 'GT',
      dryWeight: vehicle.dryWeight || '',
      mainDriverObj: driverObj,
      templateObj: tmplObj
    });
  };

  // 3. Salva Modifiche
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editVehicle) return;
    try {
      const ref = doc(db, "vehicles", editVehicle.id);
      await updateDoc(ref, {
        marca: editVehicle.marca,
        modello: editVehicle.modello,
        numeroGara: editVehicle.numeroGara,
        categoria: editVehicle.categoria || 'GT',
        dryWeight: Number(editVehicle.dryWeight) || null,
        passportLink: editVehicle.passportLink,
        mainDriverId: editVehicle.mainDriverObj?.id || null,
        mainDriverName: editVehicle.mainDriverObj?.nomeCompleto || null,
        templateId: editVehicle.templateObj?.id || null,
        templateName: editVehicle.templateObj?.templateName || null
      });
      setEditVehicle(null);
    } catch (err) { 
      console.error(err); 
      alert(t('edit_modal.error_save')); // <-- Tradotto
    }
  };

  // 4. Elimina Veicolo
  const handleDelete = async (e, vehicle) => {
    e.preventDefault(); e.stopPropagation();
    if (window.confirm(t('delete_confirm', { name: `${vehicle.marca} ${vehicle.modello}` }))) {
      try {
        const q = query(collection(db, "setupSheets"), where("vehicleId", "==", vehicle.id));
        const snaps = await getDocs(q);
        const batch = writeBatch(db);
        snaps.forEach(d => batch.delete(d.ref));
        batch.delete(doc(db, "vehicles", vehicle.id));
        await batch.commit();
      } catch (err) { console.error(err); alert(t('delete_error')); }
    }
  };

  // 5. Esporta Report (PDF)
  const handleExportHistory = async (e, vehicle) => {
    e.preventDefault(); e.stopPropagation();
    if (isExporting) return;
    setIsExporting(true);
    try {
      // 1. Recupera ISSUES
      const iQ = query(collection(db,"vehicleIssues"), where("vehicleId","==",vehicle.id), orderBy("createdAt","desc"));
      const iS = await getDocs(iQ);
      const issuesData = iS.docs.map(d => {
        const v = d.data();
        return {
          Data: formatDateForExcel(v.createdAt),
          Categoria: v.category,
          Stato: v.status,
          Descrizione: v.description,
          Soluzione: v.resolution || '-',
          Priorita: v.priority
        };
      });

      // 2. Recupera WORK ORDERS
      const wQ = query(collection(db,"workOrders"), where("vehicleId","==",vehicle.id), orderBy("createdAt","desc"));
      const wS = await getDocs(wQ);
      const woData = wS.docs.map(d => {
        const v = d.data();
        return {
          Data: formatDateForExcel(v.createdAt),
          Titolo: v.title,
          Stato: v.status,
          Note: v.description || '-'
        };
      });

      let proxyLogoUrl = null;
      if (teamData?.logoUrlLight) {
          proxyLogoUrl = `https://wsrv.nl/?url=${encodeURIComponent(teamData.logoUrlLight)}&output=png`;
      }

      const displayDate = new Date().toLocaleDateString();

      const blob = await pdf(
        <VehicleHistoryPdf 
          vehicle={vehicle}
          teamData={teamData || {}}
          teamLogoBase64={proxyLogoUrl}
          issuesData={issuesData}
          woData={woData}
          displayDate={displayDate}
          showWatermark={isTrial}
        />
      ).toBlob();
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `History_${vehicle.marca}_${vehicle.modello}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch(err){ 
      console.error(err); 
      alert("Errore durante l'esportazione del PDF."); 
    }
    finally { setIsExporting(false); }
  };


  // --- RENDER ---
  return (
    <>
      {/* 1. Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>{t('title')}</Typography>
        {isRestrictedUser && <Typography variant="body2" color="text.secondary">{t('viewing_assigned', { count: vehicles.length })}</Typography>}
      </Paper>

      {/* 2. Lista Veicoli */}
      {isLoading ? ( <CircularProgress sx={{ display: 'block', margin: 'auto' }} /> ) : (
        <>
          {vehicles.length === 0 && <Typography sx={{mt:2,textAlign:'center',fontStyle:'italic'}}>{isRestrictedUser?t('empty_assigned'):t('empty_general')}</Typography>}
          
          <List>
            {vehicles.map(vehicle => (
              <ListItem 
                key={vehicle.id} 
                disablePadding 
                component={RouterLink} 
                to={`/vehicle/${vehicle.id}`}
                sx={{ 
                  textDecoration: 'none', color: 'inherit', backgroundColor: 'background.paper', 
                  border: '1px solid', borderColor: 'divider', mb: 1.5, borderRadius: 2, 
                  transition: '0.2s', '&:hover': { backgroundColor: 'action.hover', transform: 'translateY(-2px)' } 
                }}
                secondaryAction={
                  isManager && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={t('actions.qrcode')}>
                        <IconButton edge="end" onClick={(e) => handleOpenQR(e, vehicle)}>
                          <QrCodeIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Esporta Storico Lavori (PDF)">
                        <IconButton edge="end" onClick={(e) => handleExportHistory(e, vehicle)} disabled={isExporting}>
                          {isExporting ? <CircularProgress size={20} /> : <PictureAsPdfIcon color="error" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('actions.edit')}>
                        <IconButton edge="end" onClick={(e) => handleOpenEdit(e, vehicle)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('actions.delete')}>
                        <IconButton edge="end" onClick={(e) => handleDelete(e, vehicle)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )
                }
              >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <DirectionsCarIcon fontSize="large" />
                </Box>

                <ListItemText 
                  primary={`${vehicle.marca} ${vehicle.modello}`} 
                  secondary={
                    <React.Fragment>
                      <Typography component="span" variant="body2" color="text.primary" fontWeight="bold">
                        #{vehicle.numeroGara || 'N/A'}
                      </Typography>
                      {` — ${vehicle.mainDriverName || t('no_driver')}`}
                      <br/>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {vehicle.templateName}
                      </Typography>
                    </React.Fragment>
                  }
                  sx={{ my: 1 }} 
                  primaryTypographyProps={{ fontWeight: 700, fontSize: '1.2rem' }} 
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
      
      {/* 3. Form Aggiungi */}
      {isManager && (
        <>
          <Divider sx={{ my: 4 }} />
          {teamData?.maxCars > 0 && vehicles.length >= teamData.maxCars ? (
            <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'warning.main', borderRadius: 2, backgroundColor: 'warning.light' }}>
              <Typography variant="h6" color="warning.dark">
                Hai raggiunto il limite massimo di auto previste dal tuo piano ({teamData.maxCars}).
              </Typography>
              <Typography variant="body2" color="warning.dark">
                Contatta l'amministratore per richiedere un upgrade o elimina un'auto esistente.
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleAddVehicle} sx={{ mt: 3 }}>
              <Typography variant="h5" component="h2" gutterBottom>{t('add_title')}</Typography>
              
              <Box sx={rowStyle}> 
              <TextField required fullWidth label={t('make')} value={marca} onChange={(e) => setMarca(e.target.value)} disabled={isLoadingDrivers} />
              <TextField required fullWidth label={t('model')} value={modello} onChange={(e) => setModello(e.target.value)} disabled={isLoadingDrivers} />
            </Box>
            <Box sx={rowStyle}>
              <Autocomplete
                options={templatesList} getOptionLabel={(o) => o.templateName || ''}
                value={selectedTemplate} onChange={(e, v) => setSelectedTemplate(v)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={isLoadingTemplates} disabled={isLoadingTemplates}
                renderInput={(params) => (<TextField {...params} label={t('setup_template')} required fullWidth InputProps={{...params.InputProps, endAdornment: (<>{isLoadingTemplates && <CircularProgress size={20} />}{params.InputProps.endAdornment}</>)}} />)}
                sx={{ flex: 1 }}
              />
              <TextField fullWidth label={t('race_nr')} value={numeroGara} onChange={(e) => setNumeroGara(e.target.value)} sx={{ flex: 1 }} />
              <FormControl sx={{ flex: 1 }}>
                <InputLabel>Categoria</InputLabel>
                <Select value={categoria} label="Categoria" onChange={(e) => setCategoria(e.target.value)}>
                  <MenuItem value="GT">GT</MenuItem>
                  <MenuItem value="Formula">Formula</MenuItem>
                  <MenuItem value="Legend">Legend</MenuItem>
                  <MenuItem value="Nascar">Nascar</MenuItem>
                  <MenuItem value="V8-Super">V8-Super</MenuItem>
                  <MenuItem value="Kart">Kart</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={rowStyle}> 
              <Autocomplete
                options={driversList} getOptionLabel={(o) => o.nomeCompleto || ''}
                value={selectedDriver} onChange={(e, v) => setSelectedDriver(v)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={isLoadingDrivers} disabled={isLoadingDrivers}
                renderInput={(params) => (<TextField {...params} label={t('main_driver')} fullWidth InputProps={{...params.InputProps, endAdornment: (<>{isLoadingDrivers && <CircularProgress size={20} />}{params.InputProps.endAdornment}</>)}} />)}
                sx={{ flex: 1 }}
              />
              <TextField fullWidth label={t('dry_weight')} type="number" value={dryWeight} onChange={(e) => setDryWeight(e.target.value)} inputProps={{ step: "0.1", min: "0" }} sx={{ flex: 1 }} InputProps={{endAdornment: <InputAdornment position="end">kg</InputAdornment>}} />
            </Box>
            <Box sx={rowStyle}>
              <TextField fullWidth label={t('passport_link')} type="url" value={passportLink} onChange={(e) => setPassportLink(e.target.value)} sx={{ flex: 1 }} />
            </Box>
            <Button type="submit" variant="contained" startIcon={<AddIcon />} size="large" sx={{ mt: 1, height: '56px' }} disabled={isLoadingDrivers}>
              {t('add_button')}
            </Button>
          </Box>
          )}
        </>
      )}

      {/* --- MODALE QR CODE --- */}
      <Dialog open={!!qrVehicle} onClose={() => setQrVehicle(null)}>
        <DialogTitle>{t('actions.qrcode')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
          {qrVehicle && (
            <>
              <QRCode value={`${window.location.origin}/vehicle/${qrVehicle.id}`} size={200} />
              <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>{qrVehicle.marca} {qrVehicle.modello}</Typography>
              <Typography variant="caption">#{qrVehicle.numeroGara}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
            <Button onClick={() => window.print()}>Stampa</Button>
            <Button onClick={() => setQrVehicle(null)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* --- MODALE EDIT --- */}
      <Dialog open={!!editVehicle} onClose={() => setEditVehicle(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('edit_modal.title')}</DialogTitle>
        <Box component="form" onSubmit={handleSaveEdit}>
            <DialogContent>
                {editVehicle && (
                    <>
                        <TextField margin="normal" label={t('make')} fullWidth value={editVehicle.marca} onChange={(e) => setEditVehicle({...editVehicle, marca: e.target.value})} required />
                        <TextField margin="normal" label={t('model')} fullWidth value={editVehicle.modello} onChange={(e) => setEditVehicle({...editVehicle, modello: e.target.value})} required />
                        <Box sx={{display:'flex', gap:2, mt:1}}>
                            <TextField margin="normal" label={t('race_nr')} fullWidth value={editVehicle.numeroGara} onChange={(e) => setEditVehicle({...editVehicle, numeroGara: e.target.value})} />
                            <FormControl fullWidth margin="normal">
                              <InputLabel>Categoria</InputLabel>
                              <Select value={editVehicle.categoria || 'GT'} label="Categoria" onChange={(e) => setEditVehicle({...editVehicle, categoria: e.target.value})}>
                                <MenuItem value="GT">GT</MenuItem>
                                <MenuItem value="Formula">Formula</MenuItem>
                                <MenuItem value="Legend">Legend</MenuItem>
                                <MenuItem value="Nascar">Nascar</MenuItem>
                                <MenuItem value="V8-Super">V8-Super</MenuItem>
                                <MenuItem value="Kart">Kart</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField margin="normal" label={t('dry_weight')} type="number" fullWidth value={editVehicle.dryWeight} onChange={(e) => setEditVehicle({...editVehicle, dryWeight: e.target.value})} InputProps={{endAdornment: <InputAdornment position="end">kg</InputAdornment>}} />
                        </Box>
                        <Autocomplete
                            sx={{mt:2}} options={driversList} getOptionLabel={(o)=>o.nomeCompleto||''}
                            value={editVehicle.mainDriverObj || null}
                            onChange={(e, v) => setEditVehicle({...editVehicle, mainDriverObj: v})}
                            isOptionEqualToValue={(o,v)=>o.id===v.id}
                            renderInput={(p)=><TextField {...p} label={t('main_driver')} />}
                        />
                        <Autocomplete
                            sx={{mt:2}} options={templatesList} getOptionLabel={(o)=>o.templateName||''}
                            value={editVehicle.templateObj || null}
                            onChange={(e, v) => setEditVehicle({...editVehicle, templateObj: v})}
                            isOptionEqualToValue={(o,v)=>o.id===v.id}
                            renderInput={(p)=><TextField {...p} label={t('setup_template')} required />}
                        />
                        <TextField margin="normal" label={t('passport_link')} fullWidth type="url" value={editVehicle.passportLink} onChange={(e) => setEditVehicle({...editVehicle, passportLink: e.target.value})} />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setEditVehicle(null)}>{t('edit_modal.cancel')}</Button>
                <Button type="submit" variant="contained">{t('edit_modal.save')}</Button>
            </DialogActions>
        </Box>
      </Dialog>

    </>
  );
}

export default Garage;